import Button from '@/components/Button'
import { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import CancelBookingPopups from '@/compositions/CancelBookingPopups'
import { useBooking } from '@/context/booking'
import { useCancelBooking } from '@/context/cancelBooking'
import { CalendarUrl, createCalendarUrl } from '@/lib/calendarUrl'
import { makeBooking } from '@/lib/makeBooking'
import { SessionCard } from '@/shared/api-generated-types'
import AddToCalendar from '@/widgets/AddToCalendar'
import BadgeInfo from '@/widgets/BadgeInfo'
import BookingSuccess from '@/widgets/BookingSuccess'
import BookSession from '@/widgets/BookSession'
import InviteFriend from '@/widgets/InviteFriend'
import Modal from '@/widgets/Modal'
import { useRouter } from 'next/router'
import React from 'react'

interface DisplayPrice {
  currencyCode: string
  currencySymbol: string
  amount: number
}

function DisplayPrice({
  displayPrice,
  displayPriceDecoration,
}: {
  displayPrice?: DisplayPrice
  displayPriceDecoration?: string
}): JSX.Element | null {
  if (displayPriceDecoration && displayPriceDecoration === 'hidden') return null

  if (!displayPrice) {
    return (
      <Text size="xl" weight="sm">
        FREE
      </Text>
    )
  }

  const displayPriceValuedAt = `${displayPrice.currencySymbol}${displayPrice.amount}`

  return (
    <Text size="xs" weight="sm">
      <del>{displayPriceValuedAt}</del>&nbsp;
      <span>FREE</span>
    </Text>
  )
}

export interface Props {
  classId?: string
  className?: string
  title?: string
  subtitle?: string
  displayPrice?: DisplayPrice
  displayPriceDecoration?: string
  items: Array<SessionCard>
}

const UpcomingSessionsBlock = (props: Props): JSX.Element => {
  const router = useRouter()
  const { state: bookingState, dispatch: bookingDispatch } = useBooking()
  const { state: cancelState, dispatch: cancelDispatch } = useCancelBooking()
  const [bookingSuccess, setBookingSuccess] = React.useState<boolean>(false)
  const [openAddtoCalendar, setOpenAddtoCalendar] = React.useState<boolean>(false)
  const [openInviteFriend, setOpenInviteFriend] = React.useState<boolean>(false)
  const [cancelBookingConfirmPopup, setCancelBookingConfirmPopup] = React.useState<boolean>(false)
  const [cancelSessionId, setCancelSessionId] = React.useState<string | undefined>(undefined)
  const [calendarUrl, setCalendarUrl] = React.useState<CalendarUrl>({
    googleCalendarUrl: '',
    yahooCalendarUrl: '',
    outlookCalendarUrl: '',
    iCalUrl: '',
  })
  const { className, items, subtitle, displayPrice, displayPriceDecoration } = props
  const title = props.title || 'Upcoming sessions'

  const handleCancelBookingAction = (sessionId: string | undefined): void => {
    setCancelSessionId(sessionId)
    toggleCancelBookingConfirmPopup()
  }

  const handleBookingAction = (session: Partial<SessionCard>): void => {
    const { sessionId } = session
    bookingDispatch({ type: 'FETCHING', payload: { sessionId } })
    makeBooking({ classId: props.classId, sessionId }).then((response) => {
      if (response) {
        bookingDispatch({ type: 'BOOKED', payload: { sessionId } })
        setCalendarUrl(createCalendarUrl(session))
        toggleBookingSuccess()
      } else bookingDispatch({ type: 'FAILED', payload: { sessionId } })
    })
  }
  const toggleBookingSuccess = (): void => {
    setBookingSuccess(!bookingSuccess)
  }
  const toggleCancelBookingConfirmPopup = (): void => {
    setCancelBookingConfirmPopup(!cancelBookingConfirmPopup)
  }
  const handleScheduleRoute = (): void => {
    router.push(`/schedule/browse`)
  }

  return (
    <div className={className}>
      {bookingSuccess ? (
        <Modal onDismiss={toggleBookingSuccess}>
          {openAddtoCalendar ? (
            <AddToCalendar
              onDismiss={() => setOpenAddtoCalendar(false)}
              googleCalendarUrl={calendarUrl.googleCalendarUrl}
              yahooCalendarUrl={calendarUrl.yahooCalendarUrl}
              outlookCalendarUrl={calendarUrl.outlookCalendarUrl}
              iCalUrl={calendarUrl.iCalUrl}
            />
          ) : openInviteFriend ? (
            <InviteFriend onDismiss={() => setOpenInviteFriend(false)} />
          ) : (
            <BookingSuccess
              onClickAddToCalendar={() => setOpenAddtoCalendar(true)}
              onClickInviteFriend={() => setOpenInviteFriend(true)}
            />
          )}
        </Modal>
      ) : null}
      <div className="mb-xs">
        <CancelBookingPopups
          classId={props.classId}
          sessionId={cancelSessionId}
          sessionName={items[0].title}
          cancelDispatch={cancelDispatch}
          cancelBookingConfirmPopup={cancelBookingConfirmPopup}
          toggleCancelBookingConfirmPopup={toggleCancelBookingConfirmPopup}
        />
        <Text className="fs-md-lte-tablet" responsiveSize={{ lg: 'lg', md: 'md' }} weight="lg" tag="h2">
          {title}
        </Text>
        {subtitle && (
          <Text size="xs" weight="sm">
            {subtitle}
          </Text>
        )}
        <DisplayPrice displayPrice={displayPrice} displayPriceDecoration={displayPriceDecoration} />
      </div>
      {!items.length ? (
        <Stack y spaceBetweenY="sm">
          <Text size="xs" weight="sm">
            We currently have no upcoming sessions for this class.
          </Text>
          <div>
            <Button label="Browse Classes" theme="primary" size="sm" onClick={handleScheduleRoute} />
          </div>
        </Stack>
      ) : (
        items.map((session) => (
          <BookSession
            key={session.sessionId}
            session={session}
            bookingState={bookingState}
            cancelState={cancelState}
            handleBookingAction={handleBookingAction}
            handleCancelBookingAction={handleCancelBookingAction}
          />
        ))
      )}
      <BadgeInfo />
    </div>
  )
}

export default UpcomingSessionsBlock
